// Answers the question "someone clicked my ad - what happened afterward?"
// for the two channels that live entirely on this website: Meta Ads and
// Google. Captures how a visitor arrived on their first page load, stores
// it, and Checkout.jsx attaches it to the order they eventually place -
// so every completed order in Firestore carries a channel label, not just
// an anonymous pixel event that only Meta or GA4 can see.
//
// This deliberately mirrors GA4's own default channel grouping logic
// (fbclid/gclid presence, utm_source/medium, referrer host) rather than
// inventing a different taxonomy, so the numbers here and in GA4/Ads
// Manager tell the same story instead of contradicting each other.
//
// WHAT THIS CANNOT DO: WhatsApp and phone leave the website the instant
// someone clicks - there is no way for client-side JS to observe a WhatsApp
// conversation or a phone call. metaPixel.js's trackPixelContact() and
// firebase.js's logContactClick() cover the CLICK stage of those two
// funnels (both are called from WhatsAppButton.jsx, Footer.jsx and
// Contact.jsx). Whether that click became an order requires either a human
// to log it (not built - would mean a manual order-entry feature in
// AdminDashboard) or a paid call/WhatsApp-tracking service. Don't extend
// this file to pretend otherwise.
const STORAGE_KEY = 'akhtar_sabir_attribution'

// A click-based touch older than this is treated as stale and replaced by
// whatever the current visit looks like (which may itself resolve to
// Direct/organic if there's no new campaign signal). 30 days is a generous
// but standard last-click lookback window - long enough that someone who
// saw an ad on Monday and orders on the weekend is still credited to it,
// short enough that a browser's attribution doesn't stay pinned to an ad
// clicked six months ago.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

// Recognized ad/social hosts that should read as their brand name rather
// than a raw hostname like "l.instagram.com" or "m.facebook.com".
const REFERRER_BRANDS = [
  { test: /(^|\.)google\./, label: 'Google' },
  { test: /(^|\.)facebook\.com$/, label: 'Facebook' },
  { test: /(^|\.)instagram\.com$/, label: 'Instagram' },
  { test: /(^|\.)l\.instagram\.com$/, label: 'Instagram' },
  { test: /(^|\.)m\.facebook\.com$/, label: 'Facebook' },
  { test: /(^|\.)whatsapp\.com$/, label: 'WhatsApp' },
  { test: /(^|\.)wa\.me$/, label: 'WhatsApp' },
  { test: /(^|\.)tiktok\.com$/, label: 'TikTok' },
  { test: /(^|\.)bing\.com$/, label: 'Bing' }
]

const brandFromHost = (host) => {
  const match = REFERRER_BRANDS.find((entry) => entry.test.test(host))
  return match ? match.label : host
}

// Turns raw signals from one page load into the single channel label this
// site reports everywhere (order records, the admin dashboard's revenue
// breakdown). `medium` is a coarser bucket ('paid_social', 'paid_search',
// 'organic_social', 'organic_search', 'referral', 'direct') for grouping
// when the exact label doesn't matter.
const classify = ({ fbclid, gclid, utmSource, utmMedium, referrerHost }) => {
  // A click ID is a much stronger signal than a referrer - it proves a
  // specific ad was clicked, not just that traffic arrived from a domain
  // that also happens to host organic content.
  if (fbclid) return { channel: 'Meta Ads', medium: 'paid_social', source: 'facebook', campaign: null }
  if (gclid) return { channel: 'Google Ads', medium: 'paid_search', source: 'google', campaign: null }

  if (utmSource) {
    const src = utmSource.toLowerCase()
    const medium = (utmMedium || '').toLowerCase()
    const isPaid = /cpc|paid|ads?/.test(medium)
    const label = src.charAt(0).toUpperCase() + src.slice(1)
    return {
      channel: `${label} (${isPaid ? 'Paid' : 'Organic'})`,
      medium: isPaid ? 'paid_other' : 'organic_other',
      source: src,
      campaign: null
    }
  }

  if (referrerHost) {
    const brand = brandFromHost(referrerHost)
    if (brand === 'Google') return { channel: 'Google (Organic)', medium: 'organic_search', source: 'google', campaign: null }
    if (brand === 'Bing') return { channel: 'Bing (Organic)', medium: 'organic_search', source: 'bing', campaign: null }
    if (brand === 'Facebook' || brand === 'Instagram') {
      return { channel: `${brand} (Organic)`, medium: 'organic_social', source: brand.toLowerCase(), campaign: null }
    }
    if (brand === 'WhatsApp') return { channel: 'WhatsApp (Shared Link)', medium: 'referral', source: 'whatsapp', campaign: null }
    if (brand === 'TikTok') return { channel: 'TikTok (Organic)', medium: 'organic_social', source: 'tiktok', campaign: null }
    return { channel: `Referral: ${brand}`, medium: 'referral', source: brand, campaign: null }
  }

  return { channel: 'Direct', medium: 'direct', source: null, campaign: null }
}

const readCurrentVisitSignals = () => {
  const params = new URLSearchParams(window.location.search)
  let referrerHost = null
  try {
    referrerHost = document.referrer ? new URL(document.referrer).hostname : null
  } catch {
    referrerHost = null
  }
  // A referrer on the site's own domain (an internal SPA navigation that
  // still populates document.referrer, or a bookmark saved mid-visit) is
  // not a new touch - treat it the same as no referrer at all.
  if (referrerHost === window.location.hostname) referrerHost = null

  return {
    fbclid: params.get('fbclid'),
    gclid: params.get('gclid'),
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
    referrerHost
  }
}

const hasNewCampaignSignal = (signals) => Boolean(signals.fbclid || signals.gclid || signals.utmSource)

// Called once per page load (see App.jsx). Writes to localStorage only
// when there's a reason to: nothing stored yet, the stored touch has
// expired, or this visit carries a fresh ad-click/UTM signal that should
// take precedence over whatever was stored before (a new ad click always
// wins over a 3-week-old "Direct" from someone just re-opening the site).
export const captureAttribution = () => {
  try {
    const signals = readCurrentVisitSignals()
    const existingRaw = localStorage.getItem(STORAGE_KEY)
    const existing = existingRaw ? JSON.parse(existingRaw) : null
    const isExpired = !existing || Date.now() - existing.capturedAt > MAX_AGE_MS

    if (!isExpired && !hasNewCampaignSignal(signals)) return

    const classified = classify(signals)
    const record = {
      ...classified,
      campaign: signals.utmCampaign || classified.campaign,
      landingPage: window.location.pathname,
      capturedAt: Date.now()
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
  } catch {
    // localStorage can throw in private-browsing modes on some browsers -
    // attribution is a nice-to-have, never worth breaking the site over.
  }
}

const FALLBACK = { channel: 'Direct', medium: 'direct', source: null, campaign: null, landingPage: null, capturedAt: null }

// Called once, at the moment an order is placed (Checkout.jsx). Returns a
// plain object safe to send to the Worker - see buildValidatedOrder in
// worker/src/pricing.js, which re-validates and whitelists this server-side
// rather than trusting it outright, the same way every other client input
// is treated.
export const getStoredAttribution = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return FALLBACK
    const parsed = JSON.parse(raw)
    return { ...FALLBACK, ...parsed }
  } catch {
    return FALLBACK
  }
}
